import React, { useState } from 'react';
import { string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './DuplicateListingButton.module.css';

/**
 * DuplicateListingButton — lets providers clone a listing.
 *
 * Usage (in ManageListingsPage, listing action menu):
 *   {features.listingDuplicator && (
 *     <DuplicateListingButton
 *       listingId={listing.id.uuid}
 *       onSuccess={newListing => history.push(`/l/${newListing.id.uuid}/edit`)}
 *     />
 *   )}
 */
const DuplicateListingButton = ({ listingId, onSuccess, className }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDuplicate = async () => {
    if (!window.confirm('Create a copy of this listing?')) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/listings/duplicate/${listingId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Failed to duplicate');
      }
      const { listing } = await res.json();
      onSuccess && onSuccess(listing);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={handleDuplicate}
        disabled={isLoading}
        className={css.button}
      >
        <svg className={css.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
        {isLoading
          ? <FormattedMessage id="DuplicateListingButton.loading" />
          : <FormattedMessage id="DuplicateListingButton.label" />}
      </button>
      {error && <p className={css.error}>{error}</p>}
    </div>
  );
};

DuplicateListingButton.defaultProps = { onSuccess: null, className: null };
DuplicateListingButton.propTypes = {
  listingId: string.isRequired,
  onSuccess: func,
  className: string,
};

export default DuplicateListingButton;
